import { createExportColumnHelper } from 'components/DataExpansionComponents/Common/commonColumnHelpers'
import { SAMPLES_COLUMNS } from './samplesColumnInfo'
import { BaseExportModal } from '../Modals/ExportModal/ExportModal'
import { useMemo } from 'react'
import { useDownload } from 'features/download-widget'
import { exportSampleRequestsV2 } from 'services/samples'
import { getDownloadStatus } from 'services/downloads'

interface SampleRequestExportModalProps {
  show: boolean
  handleClose: () => void
  filters: Record<string, unknown>
  selectedIds?: number[]
  excludeIds?: number[]
}

export default function SampleRequestExportModal(
  props: SampleRequestExportModalProps
) {
  const sampleColumnHelper = createExportColumnHelper({
    columns: SAMPLES_COLUMNS,
  })
  const { startDownload } = useDownload()

  const columns = useMemo(() => {
    return [
      {
        title: 'Order Details',
        elements: [
          sampleColumnHelper.retrieveColumn('created'),
          sampleColumnHelper.retrieveColumn('date_needed'),
          sampleColumnHelper.retrieveColumn('modified'),
          sampleColumnHelper.retrieveColumn('status_display'),
          sampleColumnHelper.retrieveColumn('location_name'),
          sampleColumnHelper.retrieveColumn('location_address'),
          sampleColumnHelper.retrieveColumn('location_firstbite_id'),
          sampleColumnHelper.retrieveColumn('line_items_count'),
        ],
      },
      {
        title: 'Requestor Info',
        elements: [
          sampleColumnHelper.retrieveColumn('user_name'),
          sampleColumnHelper.retrieveColumn('user_email'),
          sampleColumnHelper.retrieveColumn('company_name'),
          sampleColumnHelper.retrieveColumn('company_firstbite_id'),
        ],
      },
      {
        title: 'Shipping',
        elements: [
          sampleColumnHelper.retrieveColumn('ship_to_name'),
          sampleColumnHelper.retrieveColumn('contact_email'),
          sampleColumnHelper.retrieveColumn('contact_phone'),
          sampleColumnHelper.retrieveColumn('shipping_address_line1'),
          sampleColumnHelper.retrieveColumn('shipping_address_line2'),
          sampleColumnHelper.retrieveColumn('shipping_city'),
          sampleColumnHelper.retrieveColumn('shipping_state'),
          sampleColumnHelper.retrieveColumn('shipping_zip'),
          sampleColumnHelper.retrieveColumn('shipping_country'),
        ],
      },
    ]
  }, [sampleColumnHelper])

  const onSubmit = async (
    selectedCols: string[],
    exportType: 'csv' | 'xlsx'
  ) => {
    await startDownload({
      items: [{ id: 0, name: 'Exporting Sample Requests...' }],
      initiate: async () => {
        const { ordering, ...filterWithoutSort } = (props.filters ??
          {}) as Record<string, unknown>
        const response = await exportSampleRequestsV2({
          selected_columns: selectedCols,
          export_type: exportType,
          filter_params: {
            ...filterWithoutSort,
            include_ids: props.selectedIds ?? [],
            exclude_ids: props.excludeIds ?? [],
          },
          sort: ordering as string | undefined,
        })
        return { type: 'async', requestId: response.request_id }
      },
      getStatus: getDownloadStatus,
      label: 'Sample Requests',
    })
  }

  return (
    <BaseExportModal
      title="Select Columns for Sample Requests Download"
      subTitle="Download up to 10,000 sample requests at a time, with any of the following columns. Any filters already applied will remain active."
      submitText="Download Sample Requests"
      columns={columns}
      onSubmit={onSubmit}
      show={props.show}
      handleClose={props.handleClose}
    />
  )
}
